<template>
  <div
    v-if="jobProfile?.last_applied_template_ulid"
    class="template-link"
  >
    <v-skeleton-loader
      v-if="listLoading"
      type="text@3"
    />

    <v-card
      v-else-if="appliedTemplate"
      :to="{ name: 'job-profile-view', params: { id: appliedTemplate.ulid } }"
      variant="outlined"
      rounded="lg"
      class="pa-3"
    >
      <p class="text-body-3 text-textLight mb-1">
        Based on
        <span class="text-body-2 font-weight-medium text-textDarkest ml-1">
          {{ appliedTemplate.title }}
        </span>
      </p>
      <div class="d-flex ga-4 align-center">
        <JobProfileStatus :is-template="true" />
        <JobProfileMeta class="align-self-center" :profile="appliedTemplate" />
        <v-spacer />
        <v-btn
          v-if="showUpdateButton"
          size="small"
          color="primary"
          variant="outlined"
          rounded="lg"
          @click.prevent="onCompare"
        >
          Compare
        </v-btn>
      </div>
      <p
        v-if="showUpdateButton"
        class="text-body-3 text-success mt-1"
      >
        The template has updates
      </p>
    </v-card>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { useJobProfiles } from '@/stores/jobProfiles/jobProfiles';
import { useAppLoading } from '@/stores/app/loading';
import { useModals } from '@/stores/modals/modals';
import JobProfileMeta from '@/components/jobProfiles/editor/JobProfileMeta.vue';
import JobProfileStatus from '@/components/jobProfiles/editor/JobProfileStatus.vue';

const { jobProfile, readOnly } = storeToRefs(useCurrentJobProfile());
const { finalizedTemplates } = storeToRefs(useJobProfiles());
const { listLoading } = storeToRefs(useAppLoading());
const { setModal } = useModals();

const appliedTemplate = computed(() => finalizedTemplates.value.find(
  (t) => t.ulid === jobProfile.value?.last_applied_template_ulid,
));

const showUpdateButton = computed(() => (
  !!appliedTemplate.value
  && jobProfile.value?.locked_fields_hash !== appliedTemplate.value.locked_fields_hash
));

const onCompare = () => {
  if (!appliedTemplate.value || !jobProfile.value) return;

  setModal({
    field: 'jobProfileCompare',
    value: true,
    params: {
      oldSide: {
        isHrisProfile: false,
        profileUlid: jobProfile.value.ulid,
        revisionUlid: jobProfile.value.last_revision_ulid,
      },
      newSide: {
        isHrisProfile: false,
        profileUlid: appliedTemplate.value.ulid,
        revisionUlid: appliedTemplate.value.finalized_revision_ulid,
      },
      readOnly: readOnly.value,
    },
  });
};

onMounted(() => {
  if (jobProfile.value?.last_applied_template_ulid) {
    useJobProfiles().loadFinalizedProfileTemplates();
  }
});
</script>

<style lang="less" scoped>
.v-card {
  border-color: @stroke-default;
}
</style>
